'use client';
import React from 'react';

const Card: React.FC<React.PropsWithChildren<{ className?: string; id?: string }>> = ({ children, className, id }) => (
  <article
    className={`rounded-2xl ${className || ''}`}
    style={{ background: 'rgba(255,255,255,.08)', backdropFilter: 'blur(8px)' }}
    id={id}
  >
    {children}
  </article>
);

const Chip: React.FC<React.PropsWithChildren<{ href?: string; onClick?: () => void }>> = ({
  href = '#',
  onClick,
  children,
}) => (
  <a
    href={href}
    onClick={onClick}
    className="px-3 py-1 rounded-full hover:bg-white/25 text-sm"
    style={{ background: 'rgba(255,255,255,.14)' }}
  >
    {children}
  </a>
);

export default function TermsPage() {
  const updated = '18 ноября 2025';
  return (
    <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-gray-100">
      <header className="px-6 pt-32 pb-16 md:pt-40 md:pb-20"
        style={{ background: 'linear-gradient(rgba(0,0,0,.45), rgba(0,0,0,.25)), radial-gradient(1200px 600px at 70% -10%, rgba(255,255,255,.08), transparent)' }}>
        <div className="max-w-7xl mx-auto">
          <nav className="mb-6 flex flex-wrap gap-3 text-sm">
            <Chip href="#toc">Оглавление</Chip>
            <Chip href="#terms">Положения</Chip>
            <Chip onClick={() => window.print()}>Печать/PDF</Chip>
          </nav>

          <h1 className="text-4xl md:text-6xl font-extrabold mb-3">Общие положения и условия</h1>
          <p className="max-w-3xl text-lg md:text-xl text-gray-200">
            Правила оказания услуг кадрового агентства INTERIM: определения, условия сотрудничества, оплата,
            конфиденциальность и ответственность сторон.
          </p>
          <div className="mt-5 text-sm flex gap-2 items-center">
            <span className="px-2.5 py-1 rounded bg-white/15">Версия: 1.0</span>
            <span className="px-2.5 py-1 rounded bg-white/15">Обновлено: {updated}</span>
          </div>
        </div>
      </header>

      <main className="px-6 py-12 md:py-16 max-w-5xl mx-auto space-y-12">
        <Card className="p-6 scroll-mt-32" id="toc">
          <h2 className="text-2xl font-bold mb-3">Оглавление</h2>
          <ol className="grid md:grid-cols-2 gap-y-1 text-sm">
            {[
              '1. Термины и определения',
              '2. Предмет и услуги',
              '3. Заказ, договор и приёмка',
              '4. Тарифы, оплата и налоги',
              '5. SLA и KPI',
              '6. Обязанности Клиента',
              '7. Конфиденциальность и данные',
              '8. Интеллектуальная собственность',
              '9. Несолиситация (Non-Solicitation)',
              '10. Ответственность и лимиты',
              '11. Форс-мажор',
              '12. Срок, расторжение, пауза',
              '13. Применимое право и споры',
              '14. Изменения условий',
              '15. Контакты',
            ].map((t, i) => (
              <li key={i}>
                <a className="underline text-yellow-200" href={`#${i + 1}`}>
                  {t}
                </a>
              </li>
            ))}
          </ol>
        </Card>

        <section id="terms" className="space-y-8 text-sm md:text-base scroll-mt-32">
          <Card className="p-6" id="intro">
            <h2 className="text-2xl font-bold">Введение</h2>
            <p className="mt-2">
              Эти Общие положения и условия (далее — «Условия») регулируют оказание услуг
              компанией «INTERIM» (далее — «Исполнитель») для юридических лиц и ИП (далее —
              «Клиент»). При расхождении специальных условий договора/заказа и настоящих Условий
              приоритет имеют специальные условия.
            </p>
          </Card>

          <Card className="p-6 scroll-mt-32" id="1">
            <h3 className="text-xl font-bold">1. Термины и определения</h3>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>
                <b>Услуги RPO (подбор персонала)</b> — закрытие вакансий под KPI Клиента.
              </li>
              <li>
                <b>Аутсорсинг</b> — выполнение функции силами Исполнителя (напр., линия L1/L2).
              </li>
              <li>
                <b>Аутстаффинг</b> — оформление персонала у Исполнителя с выведением в проект.
              </li>
              <li>
                <b>KPI/SLA</b> — показатели/соглашение об уровне сервиса.
              </li>
              <li>
                <b>Материалы</b> — документы, отчёты, базы кандидатов, медиа, код/скрипты.
              </li>
            </ul>
          </Card>

          <Card className="p-6 scroll-mt-32" id="2">
            <h3 className="text-xl font-bold">2. Предмет и услуги</h3>
            <p className="mt-2">
              Исполнитель оказывает услуги согласно подтверждённому Заказу/Договору: RPO,
              аутсорсинг, аутстаффинг, консалтинг, внедрение ATS/BI, коммуникационные кампании и
              др.<br />
              Объём, сроки, команда и артефакты фиксируются в Заказе/Приложении.
            </p>
          </Card>

          <Card className="p-6 scroll-mt-32" id="3">
            <h3 className="text-xl font-bold">3. Заказ, договор и приёмка</h3>
            <ol className="list-decimal ml-6 mt-2 space-y-1">
              <li>Стороны согласуют Заказ (SOW): цели, KPI/SLA, стоимость, график.</li>
              <li>Приёмка выполняется по ежемесячным актам/отчётам либо по этапам.</li>
              <li>
                Молчаливое согласие: при отсутствии мотивированных замечаний в течение 5 рабочих дней отчёт считается принятым.
              </li>
            </ol>
          </Card>

          <Card className="p-6 scroll-mt-32" id="4">
            <h3 className="text-xl font-bold">4. Тарифы, оплата и налоги</h3>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>
                Базовые ставки калькулятора: <b>Подбор — 20%</b>, <b>Аутсорсинг — 25%</b>,{' '}
                <b>Аутстаффинг — 15%</b> (могут отличаться в Заказе).
              </li>
              <li>Модели: фикс, фикс+успех, за выход/смену, MSP.</li>
              <li>Оплата: предоплата/помесячно/по этапам, срок платежа — 5–15 банковских дней.</li>
              <li>Налоги и сборы оплачиваются согласно законодательству и условиям договора.</li>
            </ul>
          </Card>

          <Card className="p-6 scroll-mt-32" id="5">
            <h3 className="text-xl font-bold">5. Соглашение об уровне сервиса (SLA) и ключевые показатели эффективности (KPI)</h3>
            <p className="mt-2">
              Показатели (пример): скорость закрытия, конверсии этапов, явка, NPS, качество кандидатов. SLA содержит метрики, источники данных, периодичность отчётности и санкции/бонусы при отклонениях.
            </p>
          </Card>

          <Card className="p-6 scroll-mt-32" id="6">
            <h3 className="text-xl font-bold">6. Обязанности Клиента</h3>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>
                Назначить ответственных и предоставить доступы (ATS/CRM, почта, календари, бизнес-аналитика).
              </li>
              <li>
                Своевременно предоставлять требования к вакансиям, ставки/графики, обратную связь по кандидатам.
              </li>
              <li>Соблюдать SLA по откликам и согласованиям интервью.</li>
            </ul>
          </Card>

          <Card className="p-6 scroll-mt-32" id="7">
            <h3 className="text-xl font-bold">7. Конфиденциальность и данные</h3>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Стороны обязуются не раскрывать Конфиденциальную информацию без письменного согласия.</li>
              <li>
                Персональные данные обрабатываются по принципам минимизации и законности; по запросу возможна удаление/выдача копии.
              </li>
              <li>Доступы предоставляются по принципу наименьших прав, каналы шифруются.</li>
            </ul>
          </Card>

          <Card className="p-6 scroll-mt-32" id="8">
            <h3 className="text-xl font-bold">8. Интеллектуальная собственность</h3>
            <p className="mt-2">
              Право собственности на предоставленные Клиентом материалы остаётся у Клиента. Право использования результатов работ Исполнителя передаётся Клиенту в объёме, необходимом для целей Заказа, если иное не указано в договоре.
            </p>
          </Card>

          <Card className="p-6 scroll-mt-32" id="9">
            <h3 className="text-xl font-bold">9. Несолиситация</h3>
            <p className="mt-2">
              В течение срока договора и 6–12 месяцев после (период уточняется в Заказе) Стороны обязуются не нанимать напрямую сотрудников/подрядчиков другой Стороны без её согласия.
            </p>
          </Card>

          <Card className="p-6 scroll-mt-32" id="10">
            <h3 className="text-xl font-bold">10. Ответственность и лимиты</h3>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Стороны несут ответственность за прямые документально подтверждённые убытки.</li>
              <li>
                Лимит ответственности Исполнителя — не более 100% стоимости услуг за последние 3 расчётных периода по спорному Заказу.
              </li>
              <li>Исключение косвенных убытков (упущенная выгода, потеря данных и др.).</li>
            </ul>
          </Card>

          <Card className="p-6 scroll-mt-32" id="11">
            <h3 className="text-xl font-bold">11. Форс-мажор</h3>
            <p className="mt-2">
              Стороны освобождаются от ответственности за неисполнение обязательств вследствие обстоятельств непреодолимой силы (форс-мажора), подтверждённых компетентным органом, на период их действия.
            </p>
          </Card>

          <Card className="p-6 scroll-mt-32" id="12">
            <h3 className="text-xl font-bold">12. Срок, расторжение, пауза</h3>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Договор действует до полного исполнения обязательств либо до даты окончания.</li>
              <li>
                Досрочное расторжение: по соглашению Сторон либо по уведомлению за 14–30 календарных дней.
              </li>
              <li>
                Пауза возможна по письменному соглашению с фиксацией статуса работ.
              </li>
            </ul>
          </Card>

          <Card className="p-6 scroll-mt-32" id="13">
            <h3 className="text-xl font-bold">13. Применимое право и споры</h3>
            <p className="mt-2">
              Применимое право и юрисдикция определяются в Договоре/Заказе. Споры решаются путём переговоров, далее — в компетентном суде по согласованию Сторон.
            </p>
          </Card>

          <Card className="p-6 scroll-mt-32" id="14">
            <h3 className="text-xl font-bold">14. Изменения условий</h3>
            <p className="mt-2">
              Исполнитель может обновлять Условия. Новая редакция вступает в силу с даты публикации на сайте, если не указано иное. Значимые изменения коммуницируются Клиентам по e-mail.
            </p>
          </Card>

          <Card className="p-6 scroll-mt-32" id="15">
            <h3 className="text-xl font-bold">15. Контакты</h3>
            <p className="mt-2">
              <b>Исполнитель:</b> адровое агентство ИНТЕРИМ
            </p>
            <p>
              <b>E-mail:</b> info@interimjob.ru
            </p>
            <p>
              <b>Адрес:</b> 364024, Чеченская Республика, г Грозный, р-н Ахматовский, ул Им. С.Ш.Лорсанова, д. 4
            </p>
          </Card>

          <Card className="p-6">
            <h3 className="text-xl font-bold">История изменений</h3>
            <ul className="text-sm text-gray-200 mt-2">
              <li>
                <b>18.11.2025</b> — первичная публикация (v1.0).
              </li>
            </ul>
            <div className="mt-4 flex gap-2">
              <button
                onClick={() => window.print()}
                className="px-4 py-3 rounded-xl font-semibold"
                style={{ background: '#FFC300', color: '#102a43' }}
              >
                Скачать / Печать PDF
              </button>
            </div>
          </Card>
        </section>
      </main>
    </div>
  );
}